import {
  Button,
  ControlGroup,
  FileUpload,
  StatusMessage,
  Text,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { generateId } from '@/utils/id-generator'
import { logger } from '@/utils/logger'

import {
  BulkImportRecoveryResult,
  useRecoverBulkImportRunMutation,
} from './bulkImportApi'
import { BulkImportRejection, getBulkImportRejection } from './bulkImportErrors'
import { BulkImportProblemList } from './BulkImportProblemList'

export interface BulkImportRecoverPanelProps {
  runId: number
}

/**
 * Sending a corrected failure report back against the import it came from.
 *
 * Posted to the same run, never starting a second one: a corrected file describes the
 * same import, and a new run would create everyone who already succeeded a second time
 * and count them twice in everything the admin reads. What comes back is reported as an
 * attempt *appended* to the run rather than as a replacement of it.
 *
 * The file is sent exactly as it was downloaded. It carries three columns the import
 * does not read, which the backend tolerates on purpose — their presence is how it
 * tells its own export from a hand-written file — so nothing here strips them, and the
 * admin is not asked to.
 *
 * A file the backend refuses is reported the same way a first upload's refusal is, by
 * the same component. An admin correcting a file should not have to learn a second way
 * of reading what is wrong with it.
 */
export const BulkImportRecoverPanel = ({
  runId,
}: BulkImportRecoverPanelProps): React.ReactElement => {
  const { t } = useTranslation()
  const [recoverRun, { isLoading }] = useRecoverBulkImportRunMutation()

  const [file, setFile] = React.useState<File | null>(null)
  const [rejection, setRejection] = React.useState<
    BulkImportRejection | undefined
  >(undefined)
  const [result, setResult] = React.useState<
    BulkImportRecoveryResult | undefined
  >(undefined)
  const [failed, setFailed] = React.useState(false)
  const [fileError, setFileError] = React.useState<string | undefined>(
    undefined,
  )

  const clearOutcome = () => {
    setRejection(undefined)
    setResult(undefined)
    setFailed(false)
  }

  const handleFilesChosen = (files: FileList) => {
    const chosen = files.item(0)
    if (!chosen) return
    clearOutcome()
    setFileError(undefined)
    setFile(chosen)
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!file) {
      // Returning quietly leaves the admin unable to tell a request that failed from one
      // that was never made. The upload form answers the same case the same way.
      setFileError(t('BULK_IMPORT_FILE_REQUIRED'))
      return
    }
    clearOutcome()
    try {
      setResult(await recoverRun({ runId, file }).unwrap())
      // The rows this file just sent back are no longer failed ones, so posting it again
      // would be refused row by row and read as though the admin's corrections were
      // wrong. Clearing it makes the next send a deliberate act.
      setFile(null)
    } catch (error) {
      const parsed = getBulkImportRejection(error)
      if (parsed) setRejection(parsed)
      else setFailed(true)
      logger.error('Error recovering bulk import run:', error)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label={t('BULK_IMPORT_RECOVER')}
      data-testid='bulk-import-recover'
    >
      <Text tag='p'>{t('BULK_IMPORT_RECOVER_HELP')}</Text>

      {rejection && (
        <BulkImportProblemList
          rejection={rejection}
          title={t('BULK_IMPORT_RECOVER_REJECTED')}
        />
      )}
      {failed && (
        <StatusMessage
          theme='danger'
          show
          title={t('BULK_IMPORT_RECOVER_FAILED')}
        />
      )}
      {result && (
        <StatusMessage
          theme='info'
          show
          title={t('BULK_IMPORT_RECOVER_APPENDED')}
          content={
            <span data-testid='bulk-import-recover-result'>
              {[
                `${t('BULK_IMPORT_RECOVER_RETRIED')}: ${result.retried}`,
                `${t('BULK_IMPORT_RECOVER_ESCALATED')}: ${result.escalated}`,
                `${t('BULK_IMPORT_RECOVER_SKIPPED')}: ${result.skipped}`,
                `${t('BULK_IMPORT_RECOVER_NOT_APPLIED')}: ${
                  result.details_not_applied
                }`,
              ].join(' · ')}
            </span>
          }
        />
      )}

      <ControlGroup
        label={t('BULK_IMPORT_FILE')}
        helpText={fileError}
        control={
          <FileUpload
            id={generateId('input', 'bulk-import-recover-file')}
            dataTestingId={generateId('input', 'bulk-import-recover-file')}
            accept='.csv,text/csv'
            onFilesChosen={handleFilesChosen}
            label={t('BULK_IMPORT_CHOOSE_FILE')}
          >
            {t('BULK_IMPORT_CHOOSE_FILE')}
          </FileUpload>
        }
      />
      {file && (
        <Text tag='p' dataTestingId='bulk-import-recover-chosen-file'>
          {file.name}
        </Text>
      )}

      <Button
        label={t(
          isLoading
            ? 'BULK_IMPORT_RECOVER_SENDING'
            : 'BULK_IMPORT_RECOVER_SEND',
        )}
        type='submit'
        theme='primary'
        loading={isLoading}
        dataTestingId='bulk-import-recover-send'
      />
    </form>
  )
}
